import React from 'react';
import { Button } from '@/components/ui/button';
import { CaretDownIcon, CaretRightIcon } from '@phosphor-icons/react';
import { formatCurrency } from '@erp/shared-utils';
import { extractNumericValue } from './indexing';
import type { BreakdownCell } from './types';
import { cn } from '@/lib/utils';

export interface DimensionHeaderProps {
  label: string;
  expanded?: boolean;
  hasChildren?: boolean;
  onToggle?: () => void;
  displayValue?: BreakdownCell['value'] | null;
  renderLabel?: (params: {
    value: string;
    isColumn?: boolean;
    isSubColumn?: boolean;
    isRow?: boolean;
    isSubRow?: boolean;
  }) => React.ReactNode;
  isColumn?: boolean;
  isSubColumn?: boolean;
  isRow?: boolean;
  isSubRow?: boolean;
}

/**
 * Reusable dimension header component
 * Handles expansion chevrons and label rendering for row/column headers
 */
export const DimensionHeader = React.memo(function DimensionHeader({
  label,
  expanded = false,
  hasChildren = false,
  onToggle,
  displayValue,
  renderLabel,
  isColumn = false,
  isSubColumn = false,
  isRow = false,
  isSubRow = false,
}: DimensionHeaderProps) {
  const defaultRenderLabel = (value: string) => (
    <span className="text-xs font-medium break-words">{value}</span>
  );

  const labelContent = renderLabel
    ? renderLabel({ value: label, isColumn, isSubColumn, isRow, isSubRow })
    : defaultRenderLabel(label);

  return (
    <div
      className={cn(
        'flex items-start gap-1',
        isColumn && expanded && 'justify-center items-center',
      )}
    >
      {hasChildren && onToggle && (
        <Button
          variant="ghost"
          size="sm"
          className="h-4 w-4 p-0 hover:bg-muted flex-shrink-0"
          onClick={(e) => {
            e.stopPropagation();
            onToggle();
          }}
        >
          {expanded ? (
            <CaretDownIcon className="h-3 w-3" />
          ) : (
            <CaretRightIcon className="h-3 w-3" />
          )}
        </Button>
      )}
      {displayValue !== null && displayValue !== undefined ? (
        <div className="flex items-center gap-1 min-w-0 flex-1">
          <span className="min-w-0 break-words">{labelContent}</span>
          <span className="text-xs text-muted-foreground flex-shrink-0">
            ({formatCurrency(extractNumericValue(displayValue))})
          </span>
        </div>
      ) : (
        <span className="min-w-0 break-words">{labelContent}</span>
      )}
    </div>
  );
});
